'use client';

import { groupMessagesByDate } from '@/lib/groupMessagesByDate';
import type { Message } from 'ai';
import { memo, useMemo } from 'react';
import AgentChatMessage, { ThinkingSkeleton } from './agent-message';
import ChatDate from './date';
import UserChatMessage from './user-message';
export type MessageWithContext = Message & {
  context?: string | null;
};
export interface ChatHistoryProps {
  messages: Message[];
  isReplicaThinking: boolean;
  lastAssistantMessageRef?: React.RefObject<HTMLDivElement | null>;
  lastUserMessageRef?: React.RefObject<HTMLDivElement | null>;
}
const getMessageRef = (message: Message, lastAssistantMessage: Message | null | undefined, lastUserMessage: Message | null | undefined, lastAssistantMessageRef?: React.RefObject<HTMLDivElement | null>, lastUserMessageRef?: React.RefObject<HTMLDivElement | null>) => {
  if (message.id === lastAssistantMessage?.id) {
    return lastAssistantMessageRef;
  }
  if (message.id === lastUserMessage?.id) {
    return lastUserMessageRef;
  }
  return undefined;
};
export default memo(function ChatHistory({
  messages,
  isReplicaThinking,
  lastAssistantMessageRef,
  lastUserMessageRef
}: ChatHistoryProps) {
  const groupedMessages = useMemo(() => Object.entries(groupMessagesByDate(messages)), [messages]);
  const lastUserMessage = useMemo(() => {
    return messages.findLast(message => message.role === 'user') ?? null;
  }, [messages]);
  const lastAssistantMessage = useMemo(() => {
    const lastMessage = messages.at(-1);
    return lastMessage?.role === 'assistant' ? lastMessage : null;
  }, [messages]);
  return groupedMessages.map(([date, messagesForDate], index) => <div className="flex flex-col relative" key={date}>
      {index !== 0 && <ChatDate date={date} />}

      <div className="flex flex-col mb-3">
        {messagesForDate.map(message => message.role === 'user' ? <UserChatMessage key={message.id} {...message} ref={getMessageRef(message, lastAssistantMessage, lastUserMessage, lastAssistantMessageRef, lastUserMessageRef)} /> : <AgentChatMessage key={message.id} {...message} ref={getMessageRef(message, lastAssistantMessage, lastUserMessage, lastAssistantMessageRef, lastUserMessageRef)} />)}
        {/* Show replica thinking skeleton only for today's messages (the last one) */}
        {index === groupedMessages.length - 1 && isReplicaThinking && <ThinkingSkeleton ref={lastAssistantMessageRef} />}
      </div>
    </div>);
});